import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCalendar,
  FiMapPin,
  FiPackage,
  FiShield,
} from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import ProductCard from "./ProductCard";
import "../css/main.scss";

const categoryContent = [
  { category: "Gift Boxes", label: "Gifts", productId: 1 },
  { category: "Cheese", label: "Cheese", productId: 6 },
  { category: "Charcuterie", label: "Charcuterie", productId: 9 },
  { category: "Pantry", label: "Pantry", productId: 13 },
  { category: "Sweets", label: "Sweets", productId: 17 },
  { category: "Clubs", label: "Curated Collections", productId: 21 },
];

const promises = [
  { icon: FiPackage, title: "Nationwide shipping", text: "Carefully packed from our pantry" },
  { icon: FiCalendar, title: "Made for the moment", text: "Gifts, gatherings, and everyday tables" },
  { icon: FiShield, title: "Fresh arrival promise", text: "Thoughtful cold-chain protection" },
  { icon: FiMapPin, title: "Old-city spirit", text: "A neighborhood counter, delivered" },
];

const getProduct = (id) => productData.find((product) => product.id === id);

function SectionHeading({ eyebrow, title, text, link = "/shop", linkLabel = "View all" }) {
  return (
    <div className="home__section-heading">
      <div>
        <p className="home__eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        {text && <p className="home__section-copy">{text}</p>}
      </div>
      <Link to={link}>
        {linkLabel} <FiArrowRight aria-hidden="true" />
      </Link>
    </div>
  );
}

function ProductRow({ products }) {
  return (
    <div className="home__product-row">
      {products.map((product) => (
        <ProductCard compact key={product.id} product={product} />
      ))}
    </div>
  );
}

function Main() {
  const featured = productData.filter((product) => product.featured).slice(0, 4);
  const clubProducts = productData.filter((product) => product.category === "Clubs");
  const newest = productData.filter((product) => product.newArrival).slice(0, 4);
  const signature = productData.filter((product) => [5, 9, 13, 17].includes(product.id));

  return (
    <main className="home" id="main-content">
      <section className="home__hero" aria-labelledby="home-hero-title">
        <img
          alt="Artisan cheeses, charcuterie, fruit, and preserves arranged on a walnut table"
          fetchpriority="high"
          height="1200"
          src="/img/editorial/hero-feast.png"
          width="1800"
        />
        <div className="home__hero-shade" />
        <div className="home__hero-copy">
          <p className="home__eyebrow">A pantry with personality</p>
          <h1 id="home-hero-title">The table starts here.</h1>
          <p>
            Remarkable cheese, charcuterie, pantry finds, and gifts selected to
            turn any gathering into something worth remembering.
          </p>
          <div className="home__hero-actions">
            <Link className="home__button home__button--light" to="/shop?category=Gift%20Boxes">
              Shop gourmet gifts
            </Link>
            <Link className="home__button home__button--outline" to="/shop">
              Explore the pantry
            </Link>
          </div>
        </div>
      </section>

      <section className="home__editorial-links" aria-label="Featured collections">
        <Link className="home__editorial-card home__editorial-card--wine" to="/shop?category=Gift%20Boxes">
          <span>01 / Give generously</span>
          <h2>Share something delicious.</h2>
          <p>Generous gift crates for celebrations, thanks, and just because.</p>
          <strong>Browse gift boxes <FiArrowRight aria-hidden="true" /></strong>
        </Link>
        <Link className="home__editorial-card home__editorial-card--cream" to="/shop?category=Cheese">
          <span>02 / Build a board</span>
          <h2>Find your perfect pairing.</h2>
          <p>Start with an expressive cheese and add something sweet, crisp, and savory.</p>
          <strong>Shop cheese <FiArrowRight aria-hidden="true" /></strong>
        </Link>
        <Link className="home__editorial-card home__editorial-card--olive" to="/shop?category=Clubs">
          <span>03 / Keep discovering</span>
          <h2>Good taste, on repeat.</h2>
          <p>Curated discoveries chosen to keep curious eaters happily supplied.</p>
          <strong>Explore clubs <FiArrowRight aria-hidden="true" /></strong>
        </Link>
      </section>

      <section className="home__promises" aria-label="Shopping benefits">
        {promises.map(({ icon: Icon, title, text }) => (
          <article key={title}>
            <Icon aria-hidden="true" />
            <div><strong>{title}</strong><span>{text}</span></div>
          </article>
        ))}
      </section>

      <section className="home__categories home__section">
        <div className="home__section-heading home__section-heading--centered">
          <div>
            <p className="home__eyebrow">Something for every food lover</p>
            <h2>Follow your appetite.</h2>
          </div>
        </div>
        <div className="home__category-grid">
          {categoryContent.map((item) => {
            const product = getProduct(item.productId);
            return (
              <Link key={item.category} to={`/shop?category=${encodeURIComponent(item.category)}`}>
                <span className="home__category-image">
                  <img alt="" height="720" loading="lazy" src={product.image} width="720" />
                </span>
                <strong>{item.label}</strong>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="home__section home__products">
        <SectionHeading
          eyebrow="The crowd pleasers"
          title="Gourmet gift boxes"
          text="Abundant, beautifully balanced, and ready for the person who deserves excellent snacks."
          link="/shop?category=Gift%20Boxes"
          linkLabel="Shop all gifts"
        />
        <ProductRow products={featured} />
      </section>

      <section className="home__story">
        <div className="home__story-image">
          <img
            alt="An artisan cheesemonger cutting a natural-rind wheel at a wooden counter"
            height="1067"
            loading="lazy"
            src="/img/editorial/cheesemonger-story.png"
            width="1600"
          />
        </div>
        <div className="home__story-copy">
          <p className="home__eyebrow">Meet the people behind the pantry</p>
          <h2>Great food comes with a story.</h2>
          <p>
            Our point of view is simple: taste everything, know the maker, and
            share what makes it special. From cellar-aged wheels to small-batch
            preserves, every pick earns its place at the table.
          </p>
          <Link className="home__button home__button--wine" to="/about">
            Our story <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="home__section home__products home__products--cream">
        <SectionHeading
          eyebrow="A delicious ritual"
          title="Curated collections"
          text="A rotating delivery of pantry discoveries, ready to give instantly or enjoy yourself."
          link="/shop?category=Clubs"
          linkLabel="Explore every club"
        />
        <ProductRow products={clubProducts} />
      </section>

      <section className="home__section home__products">
        <SectionHeading
          eyebrow="Fresh from our tasting table"
          title="What’s new over here"
          text="A few recent arrivals with big personality and a permanent invitation to taste."
          link="/shop?sort=newest"
          linkLabel="Browse new arrivals"
        />
        <ProductRow products={newest} />
      </section>

      <section className="home__signature">
        <div className="home__signature-intro">
          <p className="home__eyebrow">The house favorites</p>
          <h2>Four ways to start a very good table.</h2>
          <p>{siteConfig.description}</p>
          <Link to="/shop">Shop signature finds <FiArrowRight aria-hidden="true" /></Link>
        </div>
        <div className="home__signature-products">
          {signature.map((product) => (
            <Link key={product.id} to={`/products/${product.slug}`}>
              <img alt={product.name} height="720" loading="lazy" src={product.image} width="720" />
              <span>{product.category}</span>
              <strong>{product.name}</strong>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Main;
